// Mobile Number Validation Utility
// Standardized validation rules for all mobile number fields in the project

export const MOBILE_VALIDATION_RULES = {
  REQUIRED_LENGTH: 10,
  MAX_LENGTH: 10,
  MIN_LENGTH: 10,
  // Comprehensive regex patterns for mobile validation
  PATTERNS: {
    // Basic 10-digit mobile number
    BASIC: /^[0-9]{10}$/,
    // Indian mobile number (starts with 6-9)
    INDIAN_MOBILE: /^[6-9][0-9]{9}$/,
    // Digits only (for input sanitization)
    DIGITS_ONLY: /^[0-9]*$/,
    // Invalid characters to remove
    NON_DIGITS: /[^0-9]/g,
    // Valid starting digits for Indian mobile
    VALID_START: /^[6-9]/,
    // Complete validation pattern with country code
    WITH_COUNTRY_CODE: /^(?:\+91|91)?[6-9][0-9]{9}$/,
    // Invalid pattern detection
    ALL_SAME_DIGITS: /^(.)\1{9}$/, // All 10 digits are the same (e.g., 1111111111)
    SEQUENTIAL_ASCENDING: /^(0123456789|1234567890|2345678901|3456789012|4567890123|5678901234|6789012345|7890123456|8901234567|9012345678)$/,
    SEQUENTIAL_DESCENDING: /^(9876543210|8765432109|7654321098|6543210987|5432109876|4321098765|3210987654|2109876543|1098765432|0987654321)$/,
    REPEATED_PAIRS: /^(\d\d)\1{4}$/, // Same 2 digits repeated 5 times (e.g., 1212121212)
    REPEATED_TRIPLETS: /^(\d\d\d)\1{3}\d$/, // Same 3 digits repeated (e.g., 1231231231)
    TOO_MANY_ZEROS: /0{6,}/, // 6 or more consecutive zeros
    INVALID_SEQUENCES: /^(0{5,}|1{5,}|2{5,}|3{5,}|4{5,}|5{5,}|6{5,}|7{5,}|8{5,}|9{5,})/ // 5+ consecutive same digits
  },
  // Valid Indian mobile number prefixes (first 4 digits)
  VALID_INDIAN_PREFIXES: [
    // Airtel
    '6000', '6001', '6002', '6003', '6004', '6005', '6006', '6007', '6008', '6009',
    '7000', '7001', '7002', '7003', '7004', '7005', '7006', '7007', '7008', '7009',
    '7400', '7401', '7402', '7403', '7404', '7405', '7406', '7407', '7408', '7409',
    '8000', '8001', '8002', '8003', '8004', '8005', '8006', '8007', '8008', '8009',
    '9000', '9001', '9002', '9003', '9004', '9005', '9006', '9007', '9008', '9009',
    // Vodafone Idea
    '6300', '6301', '6302', '6303', '6304', '6305', '6306', '6307', '6308', '6309',
    '7300', '7301', '7302', '7303', '7304', '7305', '7306', '7307', '7308', '7309',
    '8300', '8301', '8302', '8303', '8304', '8305', '8306', '8307', '8308', '8309',
    '9300', '9301', '9302', '9303', '9304', '9305', '9306', '9307', '9308', '9309',
    // Jio
    '6200', '6201', '6202', '6203', '6204', '6205', '6206', '6207', '6208', '6209',
    '7000', '7100', '7200', '7300', '7400', '7500', '7600', '7700', '7800', '7900',
    '8800', '8801', '8802', '8803', '8804', '8805', '8806', '8807', '8808', '8809',
    '9900', '9901', '9902', '9903', '9904', '9905', '9906', '9907', '9908', '9909'
    // Note: This is a simplified list. In practice, you might want to maintain a more comprehensive list
  ],
  // Key press validation patterns
  KEY_VALIDATION: {
    // Allow only digits 0-9
    ALLOWED_KEYS: /^[0-9]$/,
    // Control keys that should be allowed
    CONTROL_KEYS: [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Clear', 'Copy', 'Cut', 'Paste', 'Select', 'SelectAll'
    ],
    // Modifier keys combinations
    MODIFIER_COMBINATIONS: {
      'ctrl+a': true, // Select All
      'ctrl+c': true, // Copy
      'ctrl+v': true, // Paste
      'ctrl+x': true, // Cut
      'ctrl+z': true, // Undo
      'ctrl+y': true, // Redo
      'cmd+a': true,  // Select All (Mac)
      'cmd+c': true,  // Copy (Mac)
      'cmd+v': true,  // Paste (Mac)
      'cmd+x': true,  // Cut (Mac)
      'cmd+z': true   // Undo (Mac)
    }
  }
};

export const MOBILE_ERROR_MESSAGES = {
  REQUIRED: "Mobile Number is required",
  INVALID_FORMAT: "Please enter a valid Mobile Number",
  INVALID_LENGTH: "Mobile number must be exactly 10 digits",
  TOO_SHORT: "Mobile number must be exactly 10 digits",
  TOO_LONG: "Mobile number cannot exceed 10 digits",
  INVALID_DIGITS: "Mobile number can only contain digits",
  INVALID_START_DIGIT: "Mobile number must start with 6, 7, 8, or 9",
  ALL_SAME_DIGITS: "Mobile number cannot have all same digits",
  SEQUENTIAL_PATTERN: "Mobile number cannot be in sequential order",
  REPEATED_PATTERN: "Mobile number has too many repeated digits",
  TOO_MANY_ZEROS: "Mobile number cannot have too many zeros",
  INVALID_PATTERN: "This mobile number pattern is not valid"
};

/**
 * Enhanced mobile validation with comprehensive regex patterns
 * @param {string} mobile - Mobile number to validate
 * @param {boolean} isRequired - Whether field is required
 * @param {boolean} strictIndianValidation - Whether to enforce Indian mobile format (6-9 start)
 * @returns {string} Error message or empty string if valid
 */
export const validateMobile = (mobile, isRequired = true, strictIndianValidation = false) => {
  // Step 1: Required field validation
  if (isRequired && (!mobile || !mobile.trim())) {
    return MOBILE_ERROR_MESSAGES.REQUIRED;
  }

  // If not required and empty, return no error
  if (!isRequired && (!mobile || !mobile.trim())) {
    return "";
  }

  // Step 2: Format validation - Check if contains only digits
  if (!MOBILE_VALIDATION_RULES.PATTERNS.DIGITS_ONLY.test(mobile)) {
    return MOBILE_ERROR_MESSAGES.INVALID_DIGITS;
  }

  // Step 3: Length validation
  if (mobile.length < MOBILE_VALIDATION_RULES.MIN_LENGTH) {
    return MOBILE_ERROR_MESSAGES.TOO_SHORT;
  }

  if (mobile.length > MOBILE_VALIDATION_RULES.MAX_LENGTH) {
    return MOBILE_ERROR_MESSAGES.TOO_LONG;
  }

  return ""; // No error
};

/**
 * Check for additional invalid mobile number patterns
 * @param {string} mobile - 10-digit mobile number
 * @returns {boolean} True if pattern is invalid
 */
const isInvalidMobilePattern = (mobile) => {
  // Check for patterns like 6666600000, 7777700000, etc.
  // Pattern: First 5 digits are same, last 5 digits are same (but different)
  const first5 = mobile.substring(0, 5);
  const last5 = mobile.substring(5, 10);

  // Check if first 5 are all same and last 5 are all same
  const first5AllSame = /^(.)\1{4}$/.test(first5);
  const last5AllSame = /^(.)\1{4}$/.test(last5);

  if (first5AllSame && last5AllSame) {
    return true;
  }

  // Check for alternating patterns (e.g., 6060606060)
  if (/^(\d)(\d)\1\2\1\2\1\2\1\2$/.test(mobile)) {
    return true;
  }

  // Check for patterns where same digit appears too frequently
  const digitCount = {};
  for (let digit of mobile) {
    digitCount[digit] = (digitCount[digit] || 0) + 1;
  }

  // If any digit appears more than 6 times, it's suspicious
  for (let count of Object.values(digitCount)) {
    if (count > 6) {
      return true;
    }
  }

  return false;
};

/**
 * Advanced mobile input sanitization with comprehensive regex
 * @param {string} value - Input value (can include country codes, spaces, dashes, etc.)
 * @param {boolean} allowCountryCode - Whether to handle country codes (+91, 91)
 * @returns {string} Sanitized mobile number
 */
export const sanitizeMobileInput = (value, allowCountryCode = true) => {
  if (!value) return '';

  let cleaned = value.toString();

  // Step 1: Handle country codes if allowed
  if (allowCountryCode) {
    if (cleaned.startsWith('+91')) {
      cleaned = cleaned.substring(3);
    } else if (cleaned.startsWith('91')) {
      // Only strip '91' if the total number of digits is greater than 10
      const digitsOnly = cleaned.replace(/[^0-9]/g, '');
      if (digitsOnly.length > 10) {
        cleaned = cleaned.substring(2);
      }
    }
  }

  // Step 2: Remove all non-digits using enhanced regex
  cleaned = cleaned.replace(MOBILE_VALIDATION_RULES.PATTERNS.NON_DIGITS, '');

  // Step 3: Limit to maximum length
  if (cleaned.length > MOBILE_VALIDATION_RULES.MAX_LENGTH) {
    cleaned = cleaned.substring(0, MOBILE_VALIDATION_RULES.MAX_LENGTH);
  }

  return cleaned;
};

/**
 * Handle mobile input change with comprehensive validation
 * @param {string} value - New input value
 * @param {function} setMobile - State setter for mobile
 * @param {function} setMobileError - State setter for mobile error
 * @param {boolean} isRequired - Whether field is required
 * @param {boolean} realTimeValidation - Whether to show errors in real-time
 */
export const handleMobileChange = (value, setMobile, setMobileError, isRequired = true, realTimeValidation = true) => {
  // Step 1: Input Field Validation - Sanitize and restrict input
  const sanitized = sanitizeMobileInput(value);

  // Step 2: Update mobile state with sanitized value
  setMobile(sanitized);

  // Step 3: Value Validation - Check the actual value
  if (!realTimeValidation) {
    setMobileError(""); // Don't show errors in real-time if disabled
    return sanitized;
  }

  // Don't validate if field is being cleared/reset and not required
  if (sanitized === "" && !isRequired) {
    setMobileError("");
    return sanitized;
  }

  // Validate and set error for real-time feedback
  const error = validateMobile(sanitized, isRequired);
  setMobileError(error);

  return sanitized;
};

/**
 * Format mobile number for display (adds +91 prefix)
 * @param {string} mobile - Mobile number
 * @returns {string} Formatted mobile number
 */
export const formatMobileDisplay = (mobile) => {
  if (!mobile) return '';
  return `+91 ${mobile}`;
};

/**
 * Check if mobile number is valid for submission
 * @param {string} mobile - Mobile number to check
 * @returns {boolean} True if valid for submission
 */
export const isMobileValidForSubmission = (mobile) => {
  return validateMobile(mobile, true) === "";
};

/**
 * Advanced key press validation with comprehensive scenarios
 * @param {KeyboardEvent} event - Keyboard event
 * @param {string} currentValue - Current input value
 * @param {boolean} strictMode - Whether to apply strict validation rules
 * @returns {boolean} True if key press should be allowed
 */
export const validateKeyPress = (event, currentValue, strictMode = true) => {
  const key = event.key;
  const keyCode = event.keyCode || event.which;

  // Step 1: Always allow control/navigation keys
  if (MOBILE_VALIDATION_RULES.KEY_VALIDATION.CONTROL_KEYS.includes(key)) {
    return true;
  }

  // Step 2: Handle modifier key combinations
  const isCtrlOrCmd = event.ctrlKey || event.metaKey;
  if (isCtrlOrCmd) {
    const combination = (event.ctrlKey ? 'ctrl+' : 'cmd+') + key.toLowerCase();
    if (MOBILE_VALIDATION_RULES.KEY_VALIDATION.MODIFIER_COMBINATIONS[combination]) {
      return true;
    }
  }

  // Step 3: Allow F1-F12 function keys (for browser functionality)
  if (key.startsWith('F') && key.length <= 3) {
    return true;
  }

  // Step 4: Prevent space, special characters, and letters
  if (key === ' ' || key === 'Spacebar') {
    event.preventDefault();
    return false;
  }

  // Step 5: Validate digit input using regex
  if (!MOBILE_VALIDATION_RULES.KEY_VALIDATION.ALLOWED_KEYS.test(key)) {
    event.preventDefault();
    return false;
  }

  // Step 6: Length validation - prevent exceeding max length
  const selectionStart = event.target.selectionStart || 0;
  const selectionEnd = event.target.selectionEnd || 0;
  const hasSelection = selectionStart !== selectionEnd;

  // If there's no selection and we're at max length, prevent input
  if (!hasSelection && currentValue.length >= MOBILE_VALIDATION_RULES.MAX_LENGTH) {
    event.preventDefault();
    return false;
  }

  // Step 7: Strict mode validations (Indian mobile number rules)
  if (strictMode && currentValue.length === 0) {
    // First digit should be 6-9 for Indian mobile numbers
    if (!/^[6-9]$/.test(key)) {
      event.preventDefault();
      return false;
    }
  }

  return true;
};

/**
 * Advanced paste validation with comprehensive content processing
 * @param {string} pastedContent - Content being pasted
 * @param {string} currentValue - Current input value
 * @param {object} selectionInfo - Selection start and end positions
 * @param {boolean} allowCountryCode - Whether to handle country codes
 * @returns {object} Validation result with sanitized content and metadata
 */
export const validatePaste = (pastedContent, currentValue, selectionInfo = {}, allowCountryCode = true) => {
  if (!pastedContent) {
    return { content: '', isValid: true, message: '' };
  }

  // Step 1: Advanced sanitization with country code handling
  let sanitized = sanitizeMobileInput(pastedContent, allowCountryCode);

  // Step 2: Calculate available space considering selection
  const { selectionStart = 0, selectionEnd = 0 } = selectionInfo;
  const hasSelection = selectionStart !== selectionEnd;
  const selectedLength = hasSelection ? selectionEnd - selectionStart : 0;
  const availableSpace = MOBILE_VALIDATION_RULES.MAX_LENGTH - (currentValue.length - selectedLength);

  // Step 3: Truncate if necessary
  if (sanitized.length > availableSpace) {
    sanitized = sanitized.substring(0, availableSpace);
  }

  // Step 4: Validation checks
  if (sanitized.length === 0) {
    return {
      content: '',
      isValid: false,
      message: 'Pasted content contains no valid digits'
    };
  }


  return {
    content: sanitized,
    isValid: true,
    message: `${sanitized.length} digits pasted successfully`
  };
};

/**
 * Complete validation for both input and value - use this for form submission
 * @param {string} mobile - Mobile number to validate
 * @param {string} fieldName - Name of the field (for error messages)
 * @param {boolean} isRequired - Whether field is required
 * @returns {Object} Validation result with isValid and errorMessage
 */
export const validateMobileComplete = (mobile, fieldName = "Mobile Number", isRequired = true) => {
  // Input field validation - check if input was properly sanitized
  const sanitized = sanitizeMobileInput(mobile);

  if (sanitized !== mobile) {
    return {
      isValid: false,
      errorMessage: `${fieldName} contains invalid characters`,
      sanitizedValue: sanitized
    };
  }

  // Value validation - check the actual value
  const error = validateMobile(mobile, isRequired);

  return {
    isValid: error === "",
    errorMessage: error,
    sanitizedValue: sanitized
  };
};

/**
 * Advanced mobile input props with comprehensive validation
 * @param {string} mobile - Current mobile value
 * @param {function} setMobile - Mobile setter function
 * @param {function} setMobileError - Error setter function
 * @param {boolean} isRequired - Whether field is required
 * @param {boolean} realTimeValidation - Whether to show real-time validation
 * @param {boolean} strictMode - Whether to apply strict Indian mobile validation
 * @returns {Object} Props object for input field with enhanced validation
 */
export const getMobileInputProps = (mobile, setMobile, setMobileError, isRequired = true, realTimeValidation = true, strictMode = false) => {
  return {
    type: "tel",
    value: mobile,
    maxLength: MOBILE_VALIDATION_RULES.MAX_LENGTH,
    inputMode: "numeric",
    pattern: "[0-9]*", // Simplified pattern
    autoComplete: "tel",
    placeholder: "1234567890",

    // Simplified onChange handler that allows typing
    onChange: (e) => {
      const newValue = e.target.value;
      // Simple sanitization - only allow digits and limit length
      const cleaned = newValue.replace(/[^0-9]/g, '').substring(0, MOBILE_VALIDATION_RULES.MAX_LENGTH);
      setMobile(cleaned);

      // Real-time validation feedback
      if (realTimeValidation) {
        const error = validateMobile(cleaned, isRequired, strictMode);
        setMobileError(error);
      }
    },

    // Simple key press validation - only block obvious invalid keys
    onKeyPress: (e) => {
      const key = e.key;
      // Allow digits (0-9)
      if (/[0-9]/.test(key)) {
        return; // Allow digits
      }

      // Allow control keys
      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(key)) {
        return; // Allow control keys
      }

      // Block everything else (letters, symbols, spaces, etc.)
      if (key.length === 1) { // Single character keys only
        e.preventDefault();
      }
    },

    // Simplified paste handling
    onPaste: (e) => {
      setTimeout(() => {
        const input = e.target;
        const cleaned = input.value.replace(/[^0-9]/g, '').substring(0, MOBILE_VALIDATION_RULES.MAX_LENGTH);
        setMobile(cleaned);

        if (realTimeValidation) {
          const error = validateMobile(cleaned, isRequired, strictMode);
          setMobileError(error);
        }
      }, 0);
    }
  };
};
